import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import {
  clientAdminsFiltersSchema,
  clientAdminStatuses,
  COLOR_BY_STATUS,
  useAdminSendInvite,
  useClientAdmins,
} from 'store/admin-client-admins';
import { useRouteParams } from 'utils/useIdParam';
import { useSearchParams } from 'utils/navigation';
import { useModalContext } from 'store/modal';
import { useHistory } from 'react-router-dom';
import {
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { toast } from 'react-toastify';
import { CreateAdminModal } from './atoms';

export const ClientAdminsList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id: clientId } = useRouteParams(
    ROUTES.admin.clients.details.administrators.list,
  );
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    clientAdminsFiltersSchema,
  );

  const { data: admins, isLoading } = useClientAdmins(clientId, filters);

  const { mutate: sendInvite, isLoading: isSendingInvite } =
    useAdminSendInvite();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('administrators.new_administrator'),
                  color: 'primary',
                  onClick: () => {
                    openModal({
                      component: CreateAdminModal,
                      props: { clientId },
                    });
                  },
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <ListTotal total={admins?.total} />

            <Filters
              value={filters}
              initialValues={clientAdminsFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('common.status')}
                    value={value.status}
                    initialValue={initialValues.status}
                    onChange={(v) => setFilter('status', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={clientAdminStatuses.map((status) => ({
                          label: t(`status.${status}`),
                          key: status,
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                </>
              )}
            </Filters>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={admins?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('common.full_name'),
            cell: ({ row: { original: admin } }) => {
              return (
                <Link
                  label={admin.fullName}
                  to={ROUTES.admin.clients.details.administrators.details
                    .replace(':clientId', clientId)
                    .replace(':adminId', admin.id)}
                />
              );
            },
            meta: { sort: true },
          },
          {
            id: 'email',
            header: t('common.email'),
            cell: ({ row: { original: admin } }) => {
              return admin.email;
            },
            meta: { sort: true },
          },
          {
            id: 'phone',
            header: t('form.mobile_number'),
            cell: ({ row: { original: admin } }) => {
              return admin.phone;
            },
            meta: { sort: true },
          },
          {
            id: 'role',
            header: t('permissions.role.title'),
            cell: ({ row: { original: admin } }) => admin.adminRole.name,
            meta: { sort: true },
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: admin } }) => {
              return (
                <Typography color={COLOR_BY_STATUS[admin.status]}>
                  {t(`status.${admin.status}`)}
                </Typography>
              );
            },
            meta: { sort: true },
          },
          {
            id: 'actions',
            cell: ({ row: { original: admin } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t('common.edit'),
                      onClick: () => {
                        history.push(
                          ROUTES.admin.clients.details.administrators.details
                            .replace(':clientId', clientId)
                            .replace(':adminId', admin.id),
                        );
                      },
                    },
                    {
                      label: t('actions.resend_invitation'),
                      onClick: () => {
                        sendInvite(
                          { clientId, adminId: admin.id },
                          {
                            onSuccess: () => {
                              toast(t('common.invitation_sent'), {
                                type: 'success',
                              });
                            },
                          },
                        );
                      },
                      disabled: isSendingInvite,
                    },
                  ]}
                />
              );
            },
            meta: { align: 'right' },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          admins && {
            count: admins.total,
            page: admins.page,
            pageSize: admins.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
